// src/pages/ResetPasswordPage.tsx
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { MiraalIcon, MiraalWordmark } from '../components/Brand';

const ResetPasswordPage: React.FC = () => {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError('This reset link is missing its token - please request a new one.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setIsLoading(true);
    const result = await resetPassword(token, password);
    setIsLoading(false);
    if (result.ok) setDone(true);
    else setError(result.error || 'Could not reset password.');
  };

  return (
    <div className="relative w-full h-screen overflow-y-auto bg-[#0a0a1a] flex items-center justify-center px-6 py-10">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-[400px]"
      >
        <div className="flex items-center gap-3 mb-8">
          <MiraalIcon size={36} />
          <MiraalWordmark size={20} />
        </div>

        {done ? (
          <div>
            <h2 className="text-xl font-bold text-white mb-2">Password updated</h2>
            <p className="text-sm text-[#ccc] mb-5">You can now sign in with your new password.</p>
            <button
              onClick={() => navigate('/auth')}
              className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-all flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed, #ec4899)', boxShadow: '0 4px 20px rgba(139, 92, 246, 0.35)' }}
            >
              Sign in <ArrowRight size={16} />
            </button>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-bold text-white mb-1">Set a new password</h2>
            <p className="text-sm text-[#ccc] mb-5">Choose a new password for your account.</p>

            {error && (
              <div className="mb-4 px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#ddd]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your new password"
                  className="w-full pl-10 pr-10 py-3 rounded-xl bg-[#12121f]/90 border border-[#2a2a40] text-white text-sm placeholder-[#888] focus:outline-none focus:border-[#8b5cf6]/50 focus:ring-1 focus:ring-[#8b5cf6]/10 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#ddd] hover:text-[#999] transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={isLoading}
                className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed, #ec4899)', boxShadow: '0 4px 20px rgba(139, 92, 246, 0.35)' }}
              >
                {isLoading ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                  />
                ) : 'Update password'}
              </motion.button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
